import React, { useContext } from 'react';
import styled from 'styled-components';
import { useNavigate, NavLink as NavigateLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { handleActions } from '../../components/action-handler/actions';
import { DataElementContext } from '../common/DataElementContext';
import { equalWithoutChildren } from '../../utils/fastDeepEqual';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { logNavigationEvent } from '@/analytics';
import { useAppSelector } from '@/store';

const LinkContainerComponent = styled.a((props) => props.$styleText);
const NavigateLinkComponent = styled(NavigateLink)((props) => props.$styleText);

const preventDefaultHandler = (e) => {
  e.preventDefault();
};

export const LinkContainer = (componentProps) => {
  let props = componentProps;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const revReroute = useAppSelector((state) => state.templatesConfig.revReroute);
  const dataElementContext = useContext(DataElementContext);
  let isVisible = true;

  [props, isVisible] = processComponentProps(props, dataElementContext);

  let { href, target, actionType, eventName } = props.properties;

  let inPreviewMode = false;
  if (window !== window.parent) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('wsid')) {
      inPreviewMode = true;
    }
  }

  const reroute = React.useMemo(() => {
    let ret = href;

    if (revReroute) {
      for (const routeKey in revReroute) {
        const route = revReroute[routeKey];
        if (route?.hasGroups) {
          const matches = route?.rgx?.exec?.(href);

          if (matches) {
            let parts = route.dest?.split('/');

            parts = parts.map(p => {
              let tmp = p;

              if (tmp[0] === ':') tmp = tmp.substring(1);
              if (tmp[tmp.length - 1] === '?') tmp = tmp.substring(0, tmp.length - 1);

              if (matches.groups?.[tmp]) {
                return matches.groups?.[tmp];
              }

              return tmp;
            });

            ret = parts.join('/');
            break;
          }
        } else if (href === routeKey) {
          ret = route.dest;
          break;
        }
      }
    }

    return ret;
  }, [href, revReroute]);

  if (href !== reroute) {
    //console.log('link-container', { href, reroute });
    href = reroute;
  }

  if (!isVisible) return null;

  const handleClick = (e) => {
    if (actionType === 'event' && eventName) {
      handleActions(eventName, props, navigate, dataElementContext, e);
      return;
    }

    if (target === '_self' && href.indexOf('/') === 0) {
      navigate(href);
    }
  };

  const extraDataProps = {};
  if (props?.properties) {
    Object.keys(props?.properties).forEach((key) => {
      if (key.indexOf('data-') === 0) {
        extraDataProps[key] = props?.properties[key];
      } else if (key === 'aria-label' || key === 'preventScrollReset') {
        extraDataProps[key] = props?.properties[key];
      }
    });
  }

  if (props?.properties?.title) {
    extraDataProps['title'] = props?.properties?.title;
  }

  if (actionType === 'react-router' || (actionType === 'event' && eventName === 'show-match-details')) {
    return (
      <NavigateLinkComponent
        to={href}
        className={props.className ?? ''}
        $styleText={props.styleText}
        style={props.style}
        data-node-id={props.nodeId}
        end={props.properties.end}
        // onClick={props.properties.onClick || (actionType === 'event' && eventName === 'show-match-details' ? handleClick : null)}
        onClick={!inPreviewMode ? (e) => {
          logNavigationEvent(e, props.properties, dataElementContext);

          // Execute the appropriate handler after logging the event
          if (props.properties.onClick) {
            props.properties.onClick(e);
          } else if (actionType === 'event' && eventName === 'show-match-details') {
            handleClick(e);
          }
        } : preventDefaultHandler}
        {...extraDataProps}
      >
        {componentProps.children}
      </NavigateLinkComponent>
    );
  }

  let onClick = props.properties.onClick ?? handleClick;

  if (actionType === 'external-url' && window.config.cordova === true) {
    onClick = (e) => {
      console.log('LinkContainer click(external-url, cordova)', {
        actionType,
      });

      const internalOnClick = props.properties.onClick ?? handleClick;

      try {
        internalOnClick(e);
      } catch (e) { }

      e.preventDefault();
      try {
        window.cordova.InAppBrowser.open(href, '_system');
      } catch (e) { }
    };
  }

  return (
    <LinkContainerComponent
      className={props.className ?? ''}
      href={href}
      target={target}
      $styleText={props.styleText}
      style={props.style}
      onClick={onClick}
      data-node-id={props.nodeId}
      {...extraDataProps}
    >
      {componentProps.children}
    </LinkContainerComponent>
  );
};

export default React.memo(LinkContainer, equalWithoutChildren);
